import { useState, useEffect } from 'react'
import { accountAPI, postAPI } from '../../services/api'

function DashboardPage() {
  const [accounts, setAccounts] = useState([])
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalContent: 0,
    activeSubscribers: 0,
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      const [accountsRes, allNewsRes] = await Promise.all([
        accountAPI.getAll(),
        postAPI.getAll(),
      ])

      const accountList = accountsRes.success ? (accountsRes.data || []) : []
      setAccounts(accountList)

      const allNews = allNewsRes.success ? (allNewsRes.data || []) : []
      const activeAccounts = accountList.filter(a => a.status === 'ACTIVE')

      setStats({
        totalUsers: accountList.length,
        totalContent: allNews.length,
        activeSubscribers: activeAccounts.length,
      })
    } catch (err) {
      console.error('대시보드 데이터 로드 실패:', err)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status) => {
    if (status === 'ACTIVE') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-500">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
          Active
        </span>
      )
    }
    if (status === 'TOKEN_INVALID') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500/10 text-amber-500">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
          Expiring
        </span>
      )
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-rose-500/10 text-rose-500">
        <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
        Inactive
      </span>
    )
  }

  const getPlanBadge = (account) => {
    if (account.autoUploadEnabled) {
      return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary">Pro</span>
    }
    if (account.dailyPostLimit > 25) {
      return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-indigo-500/10 text-indigo-500">Enterprise</span>
    }
    return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-slate-500/10 text-slate-500">Free</span>
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-slate-500 dark:text-slate-400 text-lg">로딩 중...</div>
      </div>
    )
  }

  return (
    <div>
      {/* 헤더 */}
      <header className="mb-8 flex justify-between items-end">
        <div>
          <h2 className="text-3xl font-black tracking-tight mb-2">대시보드</h2>
          <p className="text-slate-500 dark:text-slate-400">서비스의 주요 지표와 최근 활동을 확인하세요.</p>
        </div>
        <div className="flex gap-3">
          <button className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium">
            <span className="material-symbols-outlined text-sm">calendar_today</span>
            최근 7일
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium">
            <span className="material-symbols-outlined text-sm">download</span>
            보고서 다운로드
          </button>
        </div>
      </header>

      {/* 핵심 지표 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* 총 사용자 수 */}
        <div className="p-6 bg-white dark:bg-background-dark border border-slate-200 dark:border-slate-800 rounded-xl">
          <div className="flex justify-between items-start mb-4">
            <span className="text-sm font-medium text-slate-500 dark:text-slate-400">총 사용자 수</span>
            <div className="p-2 rounded-lg bg-primary/10 text-primary">
              <span className="material-symbols-outlined">person</span>
            </div>
          </div>
          <div className="flex items-end gap-2">
            <span className="text-3xl font-bold">{stats.totalUsers.toLocaleString()}</span>
            <span className="text-emerald-500 text-sm font-bold pb-1">+{stats.totalUsers > 0 ? '12.5' : '0'}%</span>
          </div>
        </div>

        {/* AI 이미지 생성 수 */}
        <div className="p-6 bg-white dark:bg-background-dark border border-slate-200 dark:border-slate-800 rounded-xl">
          <div className="flex justify-between items-start mb-4">
            <span className="text-sm font-medium text-slate-500 dark:text-slate-400">총 콘텐츠 수</span>
            <div className="p-2 rounded-lg bg-primary/10 text-primary">
              <span className="material-symbols-outlined">image</span>
            </div>
          </div>
          <div className="flex items-end gap-2">
            <span className="text-3xl font-bold">{stats.totalContent.toLocaleString()}</span>
            <span className="text-emerald-500 text-sm font-bold pb-1">+{stats.totalContent > 0 ? '12.8' : '0'}%</span>
          </div>
        </div>

        {/* 서비스 구독자 수 */}
        <div className="p-6 bg-white dark:bg-background-dark border border-slate-200 dark:border-slate-800 rounded-xl">
          <div className="flex justify-between items-start mb-4">
            <span className="text-sm font-medium text-slate-500 dark:text-slate-400">활성 구독자 수</span>
            <div className="p-2 rounded-lg bg-primary/10 text-primary">
              <span className="material-symbols-outlined">subscriptions</span>
            </div>
          </div>
          <div className="flex items-end gap-2">
            <span className="text-3xl font-bold">{stats.activeSubscribers.toLocaleString()}</span>
            <span className="text-emerald-500 text-sm font-bold pb-1">+{stats.activeSubscribers > 0 ? '2.4' : '0'}%</span>
          </div>
        </div>
      </div>

      {/* 성장 트렌드 차트 */}
      <div className="bg-white dark:bg-background-dark border border-slate-200 dark:border-slate-800 rounded-xl p-8 mb-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h3 className="text-xl font-bold">성장 트렌드</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">사용자 및 콘텐츠 생성 추이 (최근 7일)</p>
          </div>
          <div className="flex gap-6">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-primary"></div>
              <span className="text-xs font-medium">사용자 증가</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-slate-400"></div>
              <span className="text-xs font-medium">콘텐츠 생성</span>
            </div>
          </div>
        </div>
        <div className="h-64 w-full relative">
          <svg className="w-full h-full" preserveAspectRatio="none" viewBox="0 0 800 200">
            <defs>
              <linearGradient id="chart-poly-grad" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#1313ec" stopOpacity="0.2" />
                <stop offset="100%" stopColor="#1313ec" stopOpacity="0" />
              </linearGradient>
            </defs>
            {/* Grid Lines */}
            <line className="text-slate-100 dark:text-slate-800" stroke="currentColor" strokeWidth="1" x1="0" x2="800" y1="0" y2="0" />
            <line className="text-slate-100 dark:text-slate-800" stroke="currentColor" strokeWidth="1" x1="0" x2="800" y1="50" y2="50" />
            <line className="text-slate-100 dark:text-slate-800" stroke="currentColor" strokeWidth="1" x1="0" x2="800" y1="100" y2="100" />
            <line className="text-slate-100 dark:text-slate-800" stroke="currentColor" strokeWidth="1" x1="0" x2="800" y1="150" y2="150" />
            <line className="text-slate-100 dark:text-slate-800" stroke="currentColor" strokeWidth="1" x1="0" x2="800" y1="200" y2="200" />
            {/* Primary Data Path */}
            <path d="M0,150 Q133,120 266,160 T532,100 T800,40" fill="none" stroke="#1313ec" strokeLinecap="round" strokeWidth="3" />
            <path fill="url(#chart-poly-grad)" d="M0,150 Q133,120 266,160 T532,100 T800,40 V200 H0 Z" />
            {/* Secondary Data Path */}
            <path className="text-slate-400" d="M0,180 Q133,160 266,170 T532,140 T800,110" fill="none" stroke="currentColor" strokeDasharray="4" strokeLinecap="round" strokeWidth="2" />
          </svg>
          <div className="flex justify-around mt-4">
            <span className="text-xs text-slate-500 font-medium">월</span>
            <span className="text-xs text-slate-500 font-medium">화</span>
            <span className="text-xs text-slate-500 font-medium">수</span>
            <span className="text-xs text-slate-500 font-medium">목</span>
            <span className="text-xs text-slate-500 font-medium">금</span>
            <span className="text-xs text-slate-500 font-medium">토</span>
            <span className="text-xs text-slate-500 font-medium">일</span>
          </div>
        </div>
      </div>

      {/* 최근 가입 및 구독 현황 테이블 */}
      <div className="bg-white dark:bg-background-dark border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
        <div className="p-6 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
          <h3 className="text-xl font-bold">최근 가입 및 구독 현황</h3>
          <button className="text-primary text-sm font-semibold hover:underline">전체 보기</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 text-xs font-semibold uppercase tracking-wider">
              <tr>
                <th className="px-6 py-4">사용자 이름</th>
                <th className="px-6 py-4">계정</th>
                <th className="px-6 py-4">등록일</th>
                <th className="px-6 py-4">구독 플랜</th>
                <th className="px-6 py-4 text-center">상태</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {accounts.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-sm text-slate-500">
                    등록된 계정이 없습니다
                  </td>
                </tr>
              ) : (
                accounts.map(account => (
                  <tr key={account.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                    <td className="px-6 py-4 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-xs font-bold">
                        {account.username?.charAt(0)?.toUpperCase()}
                      </div>
                      <span className="text-sm font-medium">@{account.username}</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400">
                      {account.username || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {account.createdAt ? new Date(account.createdAt).toLocaleDateString('ko-KR') : '-'}
                    </td>
                    <td className="px-6 py-4">
                      {getPlanBadge(account)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {getStatusBadge(account.status)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default DashboardPage
